<script setup>
// Row "⋯" menu (shadcn DropdownMenu). Several actions on one row go here so
// each choice opens exactly one dialog, never a dialog on top of a dialog.
defineProps({
  // [{ title, icon?, destructive?, onClick, hidden?, disabled?, reason? }]
  // A disabled item stays in the menu, greyed out, with `reason` under it so
  // the user learns why it can't be used.
  items: { type: Array, required: true },
  label: { type: String, default: 'Row actions' },
});
</script>

<template>
  <v-menu location="bottom end" offset="4">
    <template #activator="{ props: menuProps }">
      <v-btn
        v-bind="menuProps"
        icon="mdi-dots-horizontal"
        variant="text"
        size="small"
        :aria-label="label"
      />
    </template>

    <div class="ui-menu" role="menu">
      <template v-for="item in items" :key="item.title">
        <button
          v-if="!item.hidden"
          type="button"
          role="menuitem"
          class="ui-menu__item"
          :class="{ 'ui-menu__item--destructive': item.destructive, 'ui-menu__item--disabled': item.disabled }"
          :disabled="item.disabled"
          :aria-disabled="item.disabled ? 'true' : undefined"
          :title="item.disabled ? item.reason : undefined"
          @click="item.onClick()"
        >
          <v-icon v-if="item.icon" size="16">{{ item.icon }}</v-icon>
          <span class="ui-menu__text">
            {{ item.title }}
            <span v-if="item.disabled && item.reason" class="ui-menu__reason">{{ item.reason }}</span>
          </span>
        </button>
      </template>
    </div>
  </v-menu>
</template>

<style scoped>
.ui-menu__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.ui-menu__reason {
  max-width: 220px;
  font-size: 12px;
  line-height: 1.35;
  white-space: normal;
  color: var(--muted-foreground);
}
.ui-menu__item--disabled,
.ui-menu__item--disabled:hover {
  cursor: not-allowed;
  background-color: transparent !important;
  color: var(--muted-foreground) !important;
}
.ui-menu__item--disabled .v-icon {
  color: var(--faint-foreground) !important;
}
.ui-menu {
  min-width: 192px;
  padding: 4px;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -2px rgba(0, 0, 0, 0.1);
}
.ui-menu__item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 8px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--foreground);
  font: inherit;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  outline: none;
}
.ui-menu__item .v-icon {
  color: var(--muted-foreground);
}
.ui-menu__item:hover,
.ui-menu__item:focus-visible {
  background-color: var(--accent);
}
.ui-menu__item--destructive,
.ui-menu__item--destructive .v-icon {
  color: rgb(var(--v-theme-error));
}
.ui-menu__item--destructive:hover {
  background-color: rgba(var(--v-theme-error), 0.08);
}
</style>
